import { Tab, useTheme } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Trans, useTranslation } from "react-i18next";
import BadExample1 from "portal/images/categoryCollections/badExample1.png";
import BadExample2 from "portal/images/categoryCollections/badExample2.png";
import BadExample3 from "portal/images/categoryCollections/badExample3.png";
import BadExample4 from "portal/images/categoryCollections/badExample4.png";
import CheckIcon from "@mui/icons-material/Check";
import GoodExample1 from "portal/images/categoryCollections/goodExample1.png";
import GoodExample2 from "portal/images/categoryCollections/goodExample2.png";
import GoodExample3 from "portal/images/categoryCollections/goodExample3.png";
import GoodExample4 from "portal/images/categoryCollections/goodExample4.png";
import React, { FC, useState } from "react";
import XIcon from "@mui/icons-material/Close";

enum TABS {
  GOOD = "good",
  BAD = "bad",
}

export const ImageQualityExamples: FC = () => {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const [tab, setTab] = useState(TABS.GOOD);

  const goodImageAlt = t(
    "components.categoryCollectionProfile.instructions.good.identifiable"
  );
  const goodImages = [
    { src: GoodExample1, alt: goodImageAlt },
    { src: GoodExample2, alt: goodImageAlt },
    { src: GoodExample3, alt: goodImageAlt },
    { src: GoodExample4, alt: goodImageAlt },
  ];

  const badImages = [
    {
      src: BadExample1,
      alt: t("components.categoryCollectionProfile.instructions.bad.obscured"),
    },
    {
      src: BadExample2,
      alt: t("components.categoryCollectionProfile.instructions.bad.busy"),
    },
    {
      src: BadExample3,
      alt: t(
        "components.categoryCollectionProfile.instructions.bad.badImageQuality"
      ),
    },
    {
      src: BadExample4,
      alt: t(
        "components.categoryCollectionProfile.instructions.bad.notCentered"
      ),
    },
  ];

  return (
    <TabContext value={tab}>
      <TabList
        onChange={(_, val: TABS) => setTab(val)}
        className="mb-4"
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor:
              tab === TABS.GOOD
                ? (theme) => theme.palette.success.light
                : (theme) => theme.palette.error.light,
          },
        }}
      >
        <Tab
          value={TABS.GOOD}
          style={
            tab === TABS.GOOD
              ? {
                  color: muiTheme.palette.success.light,
                }
              : undefined
          }
          label={t(
            "components.categoryCollectionProfile.instructions.titles.goodExamples"
          )}
        />
        <Tab
          value={TABS.BAD}
          style={
            tab === TABS.BAD
              ? {
                  color: muiTheme.palette.error.light,
                }
              : undefined
          }
          label={t(
            "components.categoryCollectionProfile.instructions.titles.badExamples"
          )}
        />
      </TabList>
      <TabPanel value={TABS.GOOD}>
        <>
          <ImageExamples
            images={goodImages}
            overlayIcon={
              <CheckIcon
                className="text-green-500 bg-white/65 rounded-full"
                fontSize="large"
              />
            }
          />
          <ul className="list-disc ml-4 space-y-1">
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.good.centered" />
            </li>
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.good.visibility" />
            </li>
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.good.identifiable" />
            </li>
          </ul>
        </>
      </TabPanel>
      <TabPanel value={TABS.BAD}>
        <>
          <ImageExamples
            images={badImages}
            overlayIcon={
              <XIcon
                className="text-red-500 bg-white/65 rounded-full"
                fontSize="large"
              />
            }
          />
          <ul className="list-disc ml-4 space-y-1">
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.bad.obscured" />
            </li>
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.bad.busy" />
            </li>
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.bad.badImageQuality" />
            </li>
            <li>
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.bad.notCentered" />
            </li>
          </ul>
        </>
      </TabPanel>
    </TabContext>
  );
};

interface TransBoldProps {
  i18nKey: string;
}
export const TransBold: FC<TransBoldProps> = ({ i18nKey }) => {
  const { t } = useTranslation();
  return <Trans i18nKey={i18nKey} t={t} components={[<strong key="0" />]} />;
};

interface ImageExamplesProps {
  images: { src: string; alt: string }[];
  overlayIcon?: React.ReactElement;
}

const ImageExamples: FC<ImageExamplesProps> = ({ images, overlayIcon }) => {
  return (
    <div className="flex flex-wrap gap-2 justify-between">
      {images.map(({ src, alt }) => (
        <div key={src} className="relative">
          <img
            src={src}
            alt={alt}
            className="object-contain"
            style={{
              width: "175px",
              height: "auto",
            }}
            loading="lazy"
          />
          {overlayIcon && (
            <div className="absolute top-1 left-1">{overlayIcon}</div>
          )}
        </div>
      ))}
    </div>
  );
};
